import axios, { AxiosInstance } from 'axios';

import {
  clearSessionTokens,
  getRefreshToken,
  isRefreshTokenValid,
  persistSessionTokens,
} from 'utils/sessionTokens';

import breakpoints from './breakpoints';

const { REACT_APP_DOMAIN_URL } = process.env;

export interface RefreshSessionResult {
  token: string;
  expiry: string;
  refresh_token: string;
  refresh_token_expiry: string;
}

interface RefreshResponseBody {
  token?: string;
  access_token?: string;
  expiry?: string;
  refresh_token?: string;
  refresh_token_expiry?: string;
}

let refreshClient: AxiosInstance = axios.create({
  baseURL: REACT_APP_DOMAIN_URL,
});

let inFlightRefresh: Promise<RefreshSessionResult> | null = null;

function isCompleteRefreshPayload(
  payload: RefreshResponseBody,
): payload is RefreshSessionResult & { access_token?: string } {
  const accessToken = payload.token || payload.access_token;
  return Boolean(
    accessToken && payload.expiry && payload.refresh_token && payload.refresh_token_expiry,
  );
}

async function requestRotatedSession(): Promise<RefreshSessionResult> {
  const refreshToken = getRefreshToken();
  if (!refreshToken || !isRefreshTokenValid()) {
    clearSessionTokens();
    throw new Error('Refresh token is missing or expired');
  }

  try {
    const { data } = await refreshClient.post<RefreshResponseBody>(breakpoints.token_refresh(), {
      refresh_token: refreshToken,
    });

    if (!isCompleteRefreshPayload(data)) {
      throw new Error('Invalid refresh response');
    }

    const token = (data.token || data.access_token) as string;
    persistSessionTokens({
      token,
      expiry: data.expiry,
      refresh_token: data.refresh_token,
      refresh_token_expiry: data.refresh_token_expiry,
    });

    return {
      token,
      expiry: data.expiry,
      refresh_token: data.refresh_token,
      refresh_token_expiry: data.refresh_token_expiry,
    };
  } catch (error: any) {
    if (error?.response?.status === 401 || error?.message === 'Invalid refresh response') {
      clearSessionTokens();
    }
    throw error;
  }
}

/**
 * POST /token/refresh with the current refresh token. Rotates the same session.
 * Single-flight: concurrent callers share one in-flight request.
 * Never sends an Authorization header (dedicated client, no interceptors).
 */
export function refreshSession(): Promise<RefreshSessionResult> {
  if (!inFlightRefresh) {
    inFlightRefresh = requestRotatedSession().finally(() => {
      inFlightRefresh = null;
    });
  }
  return inFlightRefresh;
}

export function setRefreshClientForTests(client: AxiosInstance) {
  refreshClient = client;
}

export function resetRefreshSessionStateForTests() {
  inFlightRefresh = null;
  refreshClient = axios.create({
    baseURL: REACT_APP_DOMAIN_URL,
  });
}
